import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import React, {useEffect} from 'react';
import Select from 'react-select';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import makeAnimated from 'react-select/animated';
import {ContentHeader} from '@components';
import {logoutUser} from '@app/store/reducers/auth';
import {toast} from 'react-toastify';
import {createProject} from '../../services/project';
import {getAllClientNames} from '../../services/client';
import {getAllUserNames} from '../../services/auth';
import handleStatus from '../../utils/ajax';

const validationSchema = Yup.object({
  projectName: Yup.string()
    .max(40, 'project name must not exceed 40 characters')
    .matches(
      /^[A-Za-z\s]+$/,
      'project name must contain only letters and spaces'
    )
    .required('project name is required'),
  website: Yup.string()
    .matches(
      /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
      'Enter correct url!'
    )
    .required('Please enter website'),
  additionalInfo: Yup.string()
    .max(500, 'project name must not exceed 500 characters')
    .matches(
      /^[A-Za-z\s]+$/,
      'project name must contain only letters and spaces'
    )
    .required('additional info is required'),
  status: Yup.string().required('Please enter project status'),
  startedAt: Yup.date().required('Please select project start date')
});

const CreateProject = () => {
  const animatedComponents = makeAnimated();
  const [assignedTo, setAssignedTo] = React.useState([]);
  const [selectedClient, setSelectedClient] = React.useState<any>({});
  const [users, setUsers] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const initialValues = {
    projectName: '',
    website: '',
    additionalInfo: '',
    status: '',
    startedAt: ''
  };

  const onSubmit = async (values: any) => {
    try {
      if (Object.keys(selectedClient).length === 0) {
        toast('Please select a client');
        return;
      }

      const resp = await createProject({
        projectName: values.projectName,
        website: values.website,
        clientId: selectedClient.value,
        projectInformation: values.additionalInfo,
        assignedTo: assignedTo.map((project: any) => project.value),
        status: values.status,
        startedAt: values.startedAt
      });
      handleStatus(resp.status, () => dispatch(logoutUser()), navigate);

      if (!resp?.data?.ok) {
        toast.warn(resp?.data?.error || 'Error while creating project');
      }

      if (resp.status === 200) {
        toast.success(resp?.data?.message || 'Project created successfully');
        navigate('/projects');
      }
    } catch (error) {
      console.error('Error while creating project:', error);
      toast.error('Error while creating project');
    }
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema
  });

  const getAllUsers = async () => {
    const res = await getAllUserNames();
    handleStatus(res.status, () => dispatch(logoutUser()), navigate);
    if (res.data.ok) {
      setUsers(
        res.data.data.map(({_id: id, name}: any) => ({value: id, label: name}))
      );
    }
  };

  const getAllClients = async () => {
    const res = await getAllClientNames();
    handleStatus(res.status, () => dispatch(logoutUser()), navigate);
    if (res.data.ok) {
      setClients(
        res.data.data.map(({_id: id, clientName}: any) => ({
          value: id,
          label: clientName
        }))
      );
    }
  };

  useEffect(() => {
    getAllUsers();
    getAllClients();
  }, []);

  return (
    <>
      <ContentHeader title="Create Project" path="/projects" />
      <section className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="card card-outline card-info">
              <div className="card-header">
                <h3 className="card-title">Create Project</h3>
              </div>
              <div className="card-body">
                <div className="w-100">
                  <div className="card card-primary">
                    <form onSubmit={formik.handleSubmit}>
                      <div className="card-body d-flex flex-column gap-3">
                        <div className="row">
                          <label
                            htmlFor="inputName"
                            className="col-sm-2 col-form-label"
                          >
                            {' '}
                            Project Name
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="text"
                              className="form-control"
                              id="inputName"
                              placeholder="projectName"
                              name="projectName"
                              maxLength={40}
                              onChange={formik.handleChange}
                              value={formik.values.projectName}
                            />
                            {formik.touched.projectName &&
                            formik.errors.projectName ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.projectName}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="row">
                          <label
                            htmlFor="inputEmail"
                            className="col-sm-2 col-form-label"
                          >
                            Website
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="text"
                              className="form-control"
                              id="inputEmail"
                              placeholder="Website"
                              name="website"
                              onChange={formik.handleChange}
                              value={formik.values.website}
                            />
                            {formik.touched.website && formik.errors.website ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.website}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Client
                          </label>
                          <div className="col-sm-10">
                            <Select
                              closeMenuOnSelect={false}
                              components={animatedComponents}
                              defaultValue={[]}
                              value={selectedClient}
                              onChange={(e: any) => setSelectedClient(e)}
                              options={clients}
                            />

                            {/* {formik.touched.client && formik.errors.client ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.client}{' '}
                              </div>
                            ) : null} */}
                          </div>
                        </div>
                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Additional Information
                          </label>
                          <div className="col-sm-10">
                            <textarea
                              className="form-control"
                              id="inputSkills"
                              placeholder="Information"
                              style={{height: '120px'}}
                              name="additionalInfo"
                              onChange={formik.handleChange}
                              value={formik.values.additionalInfo}
                            />
                            {formik.touched.additionalInfo &&
                            formik.errors.additionalInfo ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.additionalInfo}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Assigned To
                          </label>
                          <div className="col-sm-10">
                            <Select
                              closeMenuOnSelect={false}
                              components={animatedComponents}
                              defaultValue={[]}
                              isMulti
                              value={assignedTo}
                              onChange={(e: any) => setAssignedTo(e)}
                              options={users}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Started At
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="date"
                              className="form-control datetimepicker-input"
                              data-target="#reservationdate"
                              name="startedAt"
                              onChange={formik.handleChange}
                              value={formik.values.startedAt}
                            />
                            {formik.touched.startedAt &&
                            formik.errors.startedAt ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.startedAt}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="row">
                          <label
                            htmlFor="inputSkills"
                            className="col-sm-2 col-form-label"
                          >
                            Status
                          </label>
                          <div className="col-sm-10">
                            <select
                              className="form-control"
                              name="status"
                              onChange={formik.handleChange}
                              value={formik.values.status}
                            >
                              <option value="">Select</option>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="onhold">On Hold</option>
                            </select>
                            {formik.touched.status && formik.errors.status ? (
                              <div style={{color: 'red'}}>
                                {formik.errors.status}{' '}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div>
                          <button
                            type="submit"
                            className="btn btn-primary offset-md-2"
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              {/* <div className='card-footer'>
         Note:- Please make sure you enter your appropriate Goals and CreateProject
            </div> */}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CreateProject;
